/**
 * What this installation calls a partner unit.
 *
 * The archive's records are partner units, and "Partner Unit" is what the application
 * has always called them. Not every organisation uses that word — one calls them staff
 * units, another affiliate units — and the word appears in something like two hundred
 * places: page titles, table columns, field labels, tooltips, empty states, CSV
 * headers, and the sidebar.
 *
 * THE APPROACH. Every one of those places is still written in plain English with the
 * default term in it, and the term is swapped on the way to the screen. The two
 * alternatives were both worse. A message catalogue would put two hundred lookup keys
 * between the code and the words it renders — every label harder to read, every one a
 * chance to leave a key untranslated. Interpolating a variable at each site (`${many}
 * matching the filters`) is the same edit two hundred times over, and the next label
 * anybody writes silently misses it.
 *
 * Swapping at the end means a new label needs no thought at all: write "partner unit"
 * and it is renamed with the rest. It also renames the messages the Worker sends — an
 * error or an import summary is text arriving at a component like any other — so the
 * server never has to know about this file.
 *
 * WHAT IT COSTS, said plainly. This rewrites *text on its way through a component that
 * opted in*, and it cannot tell an authored label from a record that happens to contain
 * the same two words. So the components below opt in only where the text is written by
 * this application — a column heading, a field label, a notice, a page title — and
 * never where a record's own words pass through: a Card's children, an update's body,
 * a partner's description. A newsletter that says "partner unit" in its text keeps
 * saying it, which is right: the archive holds what was written, not a version of it.
 *
 * The rewriting is by name, so the six spellings below are the contract. `Partner Unit`
 * and `Partner Units` are the two the default term supplies; the sentence-case and
 * lower-case forms are what prose uses. Write the term any other way — "PARTNER UNITS"
 * in a heading — and it will not be renamed.
 */
import {
  createContext, useContext, useCallback, useEffect, useMemo, cloneElement, isValidElement, Children,
} from 'react'

/**
 * The term the application is written in. Must match PARTNER_TERM_DEFAULT in
 * src/worker/util.js: that is what the server sends when nobody has set one, and this
 * is what the client looks for in order to replace it.
 */
export const DEFAULT_TERM = {
  one: 'Partner Unit',
  many: 'Partner Units',
  // And what one of the people in such a record is called — a second term, because a
  // record and a person in it are different things and an installation may want to
  // rename one and not the other. Partner, Staff, Affiliate: which fits depends on the
  // relationship between the organisation and the people it holds records about.
  personOne: 'Partner',
  personMany: 'Partners',
  /* And the four letters in front of every code. Not a word this application chose but an
     identifier the organisation did, which is why it is here with the other two: it is the
     third thing an installation may call its own. Must match CODE_PREFIX_DEFAULT in
     src/worker/util.js — that is what the server sends when nobody has set one, and this
     is the literal the passes below look for in order to replace it. */
  codePrefix: 'S4CA',
}

/** "Staff Units" → "Staff units", for the middle of a sentence. */
const sentenceCase = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()

const isDefault = (term) =>
  term.one === DEFAULT_TERM.one &&
  term.many === DEFAULT_TERM.many &&
  (term.personOne ?? DEFAULT_TERM.personOne) === DEFAULT_TERM.personOne &&
  (term.personMany ?? DEFAULT_TERM.personMany) === DEFAULT_TERM.personMany &&
  (term.codePrefix ?? DEFAULT_TERM.codePrefix) === DEFAULT_TERM.codePrefix

/**
 * One code, with the installation's prefix in front of it.
 *
 * `S4CA-030` is what the archive stores and `ABC-030` is what an installation that has
 * renamed the prefix reads. Only at the front, and only the four letters: the number is
 * the identity and is never touched. Works on `code_and_name` too — "S4CA-030 F and Z" —
 * since that is the same string with a name after it.
 *
 * A separate function from relabelText because it is applied in the opposite place. That
 * one rewrites text this application *wrote*, and is deliberately kept away from record
 * content; this one is *for* record content — a code is data, and the whole point is to
 * draw the organisation's own identifier in front of it. Codes are the one field where
 * that is safe, because the prefix is a fixed literal rather than a word that might mean
 * something else in somebody's sentence.
 */
export function codeText(value, term) {
  const prefix = term?.codePrefix ?? DEFAULT_TERM.codePrefix
  if (prefix === DEFAULT_TERM.codePrefix || typeof value !== 'string' || !value) return value
  return value.startsWith(DEFAULT_TERM.codePrefix)
    ? prefix + value.slice(DEFAULT_TERM.codePrefix.length)
    : value
}

/**
 * One string, in the installation's term.
 *
 * Plural before singular, because "Partner Units" contains "Partner Unit" and the
 * singular pass would otherwise eat the plural and leave a stray "s" behind.
 *
 * The hyphenated form is here because a compound adjective is written that way — "a
 * partner-unit export" — and a reader who has renamed the term would notice the one
 * place that had not followed.
 */
export function relabelText(text, term) {
  if (typeof text !== 'string' || !text || isDefault(term)) return text
  const personOne = term.personOne ?? DEFAULT_TERM.personOne
  const personMany = term.personMany ?? DEFAULT_TERM.personMany

  /* The code prefix first, and by plain replacement everywhere in the string rather than
     only at the front. In authored text "S4CA" only ever names the code scheme — a column
     heading, a placeholder reading S4CA-000, a filename in a help note, an import summary
     from the Worker — so every one of them should follow the setting, and there is no other
     sense of those four letters for this to catch by mistake. Independent of the passes
     below: renaming the prefix does not rename a partner unit. */
  const prefix = term.codePrefix ?? DEFAULT_TERM.codePrefix
  if (prefix !== DEFAULT_TERM.codePrefix) {
    text = text.replaceAll(DEFAULT_TERM.codePrefix, prefix)
  }

  /* Each pass only runs when its own term has been changed. Not an optimisation: with the
     record's term left at the default, the pass below would still rewrite "partner-unit"
     as "partner unit" on its way past — the same words, but not the ones that were
     written, and this function has no business tidying anybody's hyphens. */
  const unitChanged = term.one !== DEFAULT_TERM.one || term.many !== DEFAULT_TERM.many
  const unit = unitChanged
    ? text
        .replaceAll('Partner Units', term.many)
        .replaceAll('Partner units', sentenceCase(term.many))
        .replaceAll('partner units', term.many.toLowerCase())
        .replaceAll('Partner Unit', term.one)
        .replaceAll('Partner unit', sentenceCase(term.one))
        .replaceAll('partner-unit', term.one.toLowerCase())
        .replaceAll('partner unit', term.one.toLowerCase())
    : text

  if (personOne === DEFAULT_TERM.personOne && personMany === DEFAULT_TERM.personMany) return unit

  /*
   * Then the people, and the order and the guard are both load-bearing.
   *
   * The order, because "Partner Units" contains "Partner": the records have to be renamed
   * first, or "Partner Units" would become "Affiliate Units" on its way past.
   *
   * The guard, because the pass above only fires when the record's own term has been
   * changed. Leave that at the default and "Partner Unit" is still standing when this pass
   * arrives — so it has to refuse to touch a "Partner" that is part of one. Whitespace or
   * a hyphen then "unit", either case, is what that looks like; "a partner-unit export"
   * and "partner units" are both the record, and "partners in this unit" is not.
   *
   * Plural before singular, since "Partners" contains "Partner" and the singular pass
   * would otherwise eat it and leave a stray "s".
   */
  const notUnit = String.raw`(?![-\s]+[Uu]nits?\b)`
  return unit
    .replace(new RegExp(String.raw`\bPartners\b${notUnit}`, 'g'), personMany)
    .replace(new RegExp(String.raw`\bpartners\b${notUnit}`, 'g'), personMany.toLowerCase())
    .replace(new RegExp(String.raw`\bPartner\b${notUnit}`, 'g'), personOne)
    .replace(new RegExp(String.raw`\bpartner\b${notUnit}`, 'g'), personOne.toLowerCase())
}

/**
 * The same, for anything a component may have been handed: a string, a list, or a tree
 * of elements with words in the leaves — a notice with a `<strong>` in the middle of a
 * sentence is the ordinary case.
 *
 * Elements are cloned rather than mutated, and only their children are touched: an
 * element's own props are left alone, so a `title` attribute deep inside a notice is
 * not rewritten here. The components below pass such props through relabelText by hand,
 * which is why they are the ones that decide, and not this function.
 *
 * Three things stop the walk. `dangerouslySetInnerHTML` is not ours to rewrite; a
 * function child is a render prop and calling it here would be a second render; and the
 * depth cap is a backstop against a cycle nobody expected — eight levels is deeper than
 * any notice in this application.
 */
export function relabelNode(node, term, depth = 0) {
  if (isDefault(term)) return node
  if (typeof node === 'string') return relabelText(node, term)
  if (typeof node === 'number' || node === null || node === undefined || typeof node === 'boolean') {
    return node
  }
  if (Array.isArray(node)) {
    return Children.map(node, (child) => relabelNode(child, term, depth))
  }
  if (isValidElement(node)) {
    const kids = node.props?.children
    if (
      depth >= 8 ||
      kids === null ||
      kids === undefined ||
      typeof kids === 'function' ||
      node.props?.dangerouslySetInnerHTML
    ) {
      return node
    }
    return cloneElement(node, undefined, relabelNode(kids, term, depth + 1))
  }
  return node
}

const Ctx = createContext(DEFAULT_TERM)

/**
 * The same value outside React, for the one caller that is not a component: downloadCsv,
 * which writes column headings into a file.
 *
 * Kept in step by an effect rather than during render, which is the rule React's own
 * lint enforces and — here — also the honest description of what this is for. It is a
 * frame behind the context on the very first paint, so nothing that renders may read
 * it: every such reader below takes the term from the hook. An export button is pressed
 * long after the first paint, which is why this one is safe.
 */
const outsideReact = { term: DEFAULT_TERM }

/** The installation's term, for code with no hook to call. Components use the hook. */
export const currentTerm = () => outsideReact.term

export function PartnerTermProvider({ term, children }) {
  // Both halves of each pair or the default for that pair: a term with one half missing
  // would rename the singular and leave the plural, which reads as a bug rather than as a
  // setting. The server applies the same rule; this is the client not trusting it to.
  /* Memoised, and it matters: useRelabel hands back a callback keyed on this object, so a
     fresh one per render would defeat every memo downstream of it and re-run the effect
     below on every paint. */
  const value = useMemo(
    () => ({
      one: term?.one && term?.many ? term.one : DEFAULT_TERM.one,
      many: term?.one && term?.many ? term.many : DEFAULT_TERM.many,
      personOne: term?.personOne && term?.personMany ? term.personOne : DEFAULT_TERM.personOne,
      personMany: term?.personOne && term?.personMany ? term.personMany : DEFAULT_TERM.personMany,
      codePrefix: term?.codePrefix || DEFAULT_TERM.codePrefix,
    }),
    [term?.one, term?.many, term?.personOne, term?.personMany, term?.codePrefix]
  )
  useEffect(() => {
    outsideReact.term = value
  }, [value])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** The installation's term: `{ one, many }`. Defaults outside the provider. */
export const usePartnerTerm = () => useContext(Ctx)

/**
 * `t` for a component: pass it a string or a node and render what comes back.
 *
 * The identity is stable while the term is, so a component that passes it on to a memo
 * does not defeat the memo. `me.data.partnerTerm` is a react-query cache object, so it
 * changes when the term changes and not otherwise.
 */
export function useRelabel() {
  const term = usePartnerTerm()
  return useCallback((node) => relabelNode(node, term), [term])
}

/**
 * The inverse: a code somebody typed, back to the form the archive stores.
 *
 * One canonical spelling in the database is the whole reason the display prefix is safe,
 * so the one field where a code is *entered* has to fold back to it. Type ABC-031 with the
 * prefix set to ABC and S4CA-031 is stored; type S4CA-031 and the same thing is stored.
 * Without this, creating a unit after renaming the prefix would put a second spelling in
 * the column and the archive would hold two kinds of code — exactly what not rewriting the
 * data was meant to avoid.
 */
export function codeInput(value, term) {
  const prefix = term?.codePrefix ?? DEFAULT_TERM.codePrefix
  if (prefix === DEFAULT_TERM.codePrefix || typeof value !== 'string' || !value) return value
  return value.toUpperCase().startsWith(prefix.toUpperCase())
    ? DEFAULT_TERM.codePrefix + value.slice(prefix.length)
    : value
}

/**
 * `code` for a component: pass it a stored code and render what comes back.
 *
 * A hook rather than a plain function reading `currentTerm()`, and that is not a style
 * choice. The mirror outside React is updated in an effect and is a frame behind on the
 * first paint — fine for an export button, pressed long afterwards, and wrong for a table
 * of four hundred codes, which would draw S4CA on the first frame and correct itself on
 * the second. Every code drawn during render goes through this.
 */
export function useCode() {
  const term = usePartnerTerm()
  return useCallback((value) => codeText(value, term), [term])
}

/** `codeInput` for a component, for the one field where a code is typed. */
export function useCodeInput() {
  const term = usePartnerTerm()
  return useCallback((value) => codeInput(value, term), [term])
}

/** The same for code with no hook to call — a CSV row builder. See `currentTerm`. */
export const codeOutsideReact = (value) => codeText(value, outsideReact.term)
